import { useT } from '@kmgestao/i18n';
import { Badge, PageContainer, PageIntroCard, PageSectionCard } from '@kmgestao/ui';

import { useCurrentClinicId } from '../../../features/auth/index.js';
import { ClinicShell } from '../ClinicShell.js';

import { CurrentPlanCard } from './CurrentPlanCard.js';
import { InvoiceHistoryCard } from './InvoiceHistoryCard.js';
import { NextInvoiceCard } from './NextInvoiceCard.js';

/**
 * Página `/plano` — controle financeiro da clínica perante a KmGestao
 * (PR-M2-02, 2026-04-25).
 *
 * Acesso: `clinic_owner` / `clinic_admin` (via `<RequireClinicAdmin>`).
 * Rota fica **fora** do `RequireClinicSubscriptionActive` (gate de
 * pagamento) — owner em `payment-pending` precisa **especialmente**
 * desta tela pra ver o que pagar.
 *
 * 3 cards (em ordem visual):
 *   1. **Meu plano** — nome, valor, ciclo, status, trial info, período
 *   2. **Próxima cobrança** — primeira fatura pendente + link Asaas
 *   3. **Histórico financeiro** — todas as faturas paginadas
 *      (SaaS recurring + parcelas de consultoria distinguidas)
 *
 * **Escopo importante**: trata apenas da relação **KmGestao →
 * Clinica** (cobrança da plataforma e consultoria). Cobrança da
 * **Clínica → Pacientes** é escopo M7 (Financeiro) — vai usar BYOK
 * Asaas onde cada clinic cadastra sua própria conta. Documentado em
 * `docs/clinic/financial-byok-decision.md` (PR futura).
 *
 * **Padrão canônico** das páginas internas (`PageContainer` +
 * `PageIntroCard` + `PageSectionCard`).
 */
export function ClinicPlanPage() {
  const { t } = useT('clinics');
  const clinicId = useCurrentClinicId();

  return (
    <ClinicShell>
      <PageContainer>
        <PageIntroCard
          eyebrow={t('plan.eyebrow')}
          title={t('plan.title')}
          description={t('plan.description')}
          meta={<Badge variant="outline">{t('plan.metaBadge')}</Badge>}
        />

        {!clinicId ? (
          <PageSectionCard
            title={t('plan.noClinic.title')}
            description={t('plan.noClinic.description')}
          >
            <div />
          </PageSectionCard>
        ) : (
          <>
            <CurrentPlanCard clinicId={clinicId} />
            <NextInvoiceCard clinicId={clinicId} />
            <InvoiceHistoryCard clinicId={clinicId} />
          </>
        )}
      </PageContainer>
    </ClinicShell>
  );
}
